import { DataLayerEventsEnum } from '~/types/dataLayerEventsEnum';

export enum LocationsEnum {
  PORTFOLIO_HEADER = 'portfolio_header',
  PORTFOLIO_ISSUE_LIST = 'portfolio_issue_list',
  PORTFOLIO_EMPTY = 'portfolio_empty',
  RETURNS_EMPTY = 'returns_empty',
  RETURN_DETAIL = 'return_detail',
  SETTINGS = 'settings',
  ONBOARDING = 'onboarding',
  EVENTS = 'events',
  ISSUE_DETAIL = 'issue_detail',
  RETURN_LIST = 'return_list',
  NAVBAR = 'navbar',
  ISSUE_ADDED_MANUALLY_SUCCESS = 'issue_added_manually_success',
  SYNC_TURNED_ON_SUCCESS = 'sync_turned_on_success',
  REPORT_MISSING_ISSUE_SUCCESS = 'report_missing_issue_success',
  LOGO = 'logo',
  ABOUT_COMPANY = 'about_company',
  MANAGEMENT = 'management',
  LOGIN = 'login',
  FORGOTTEN_PASSWORD_SUCCESS = 'forgotten_password_success',
  FORGOTTEN_PASSWORD = 'forgotten_password',
  ISSUE_ADD_COMPARISON = 'issue_add_comparison',
  ISSUE_ADD_INVESTMENT_DATA = 'issue_add_investment_data',
  CONSENT = 'consent',
  NO_BONDS_TO_BE_SYNCED_FOUND = 'no_bonds_to_be_synced_found',
  PREMATURE_INFO = 'premature_info',
  INVESTMENT_TRANSFER_INFO = 'investment_transfer_info',

  FOOTER = 'footer',
  HEADER = 'header',
  LOGIN_HEADER = 'login_header',
  LOGIN_FOOTER = 'login_footer',
  FORGOTTEN_PASSWORD_HEADER = 'forgotten_password_header',
  FORGOTTEN_PASSWORD_FOOTER = 'forgotten_password_footer',
  FORGOTTEN_PASSWORD_SUCCESS_HEADER = 'forgotten_password_success_header',
  FORGOTTEN_PASSWORD_SUCCESS_FOOTER = 'forgotten_password_success_footer',
  RESET_PASSWORD_HEADER = 'reset_password_header',
  RESET_PASSWORD_FOOTER = 'reset_password_footer',
  REGISTER_HEADER = 'register_header',
  REGISTER_FOOTER = 'register_footer',
  REGISTER_PASSWORD_HEADER = 'register_password_header',
  REGISTER_PASSWORD_FOOTER = 'register_password_footer',
  REGISTER_CONFIRM_HEADER = 'register_confirm_header',
  REGISTER_CONFIRM_FOOTER = 'register_confirm_footer',
  REGISTER_SUCCESS_HEADER = 'register_success_header',
  REGISTER_SUCCESS_FOOTER = 'register_success_footer',
}

export function useDataLayer() {
  const gtm = useGtm();

  function addBondClick(location: LocationsEnum) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISAddBondClick,
      location,
    });
  }

  function searchMyBondsJames(location: LocationsEnum.SETTINGS | LocationsEnum.CONSENT) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISSearchMyBondsJames,
      location,
    });
  }

  function bondsSyncOnDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISBondsSyncOnDone,
    });
  }

  function searchIssueManually(location: LocationsEnum.CONSENT
      | LocationsEnum.ONBOARDING
      | LocationsEnum.NO_BONDS_TO_BE_SYNCED_FOUND
      | LocationsEnum.ISSUE_ADDED_MANUALLY_SUCCESS
  ) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISSearchISsueManually,
      location,
    });
  }

  function chooseIssueManually() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISChooseIssueManually,
    });
  }

  function backManualAdd(location: LocationsEnum.ISSUE_ADD_COMPARISON | LocationsEnum.ISSUE_ADD_INVESTMENT_DATA) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISBackManualAdd,
      location,
    });
  }

  function addIssueManuallyDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISAddIssueManuallyDone,
    });
  }

  function turnBondsSyncOff() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISTurnBondsSyncOff,
    });
  }

  function turnMonthlyMailing(value: boolean) {
    if (value) {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnMonthlyMailingOn,
      });
    } else {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnMonthlyMailingOff,
      });
    }
  }

  function turnOffersMailing(value: boolean) {
    if (value) {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnOffersMailingOn,
      });
    } else {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnOffersMailingOff,
      });
    }
  }

  function turnInstantNotifications(value: boolean) {
    if (value) {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnInstantNotificationsOn,
      });
    } else {
      gtm?.trackEvent({
        event: DataLayerEventsEnum.ISTurnInstantNotificationsOff,
      });
    }
  }

  function changePasswordClick() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISChangePasswordClick,
    });
  }

  function changePasswordDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISChangePasswordDone,
    });
  }

  function logout() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISLogout,
    });
  }

  function openIssueDetail(location: LocationsEnum.EVENTS | LocationsEnum.PORTFOLIO_ISSUE_LIST) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenIssueDetail,
      location,
    });
  }

  function openReturnDetail(location: LocationsEnum.ISSUE_DETAIL) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenReturnDetail,
      location,
    });
  }

  function openPortfolio(location: LocationsEnum.NAVBAR
      | LocationsEnum.SYNC_TURNED_ON_SUCCESS
      | LocationsEnum.ISSUE_ADDED_MANUALLY_SUCCESS
      | LocationsEnum.LOGO
      | LocationsEnum.ISSUE_DETAIL
  ) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenPortfolio,
      location,
    });
  }

  function openReturns(location: LocationsEnum.NAVBAR
      | LocationsEnum.PORTFOLIO_HEADER
      | LocationsEnum.EVENTS
      | LocationsEnum.RETURN_DETAIL
  ) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenReturns,
      location,
    });
  }

  function openUpdates(location: LocationsEnum.ISSUE_DETAIL | LocationsEnum.PORTFOLIO_ISSUE_LIST | LocationsEnum.NAVBAR) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenUpdates,
      location,
    });
  }

  function openSettings() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenSettings,
    });
  }

  function reportMissingIssueClick() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISReportMissingIssueClick,
    });
  }

  function reportMissingIssueDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISReportMissingIssueDone,
    });
  }

  function closeConfirmationScreen(location: LocationsEnum.ISSUE_ADDED_MANUALLY_SUCCESS | LocationsEnum.SYNC_TURNED_ON_SUCCESS) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISCloseConfirmationScreen,
      location,
    });
  }

  function openIssueManagement() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenIssueManagement,
    });
  }

  function contactIssuerOpenClick(location: LocationsEnum.ISSUE_DETAIL | LocationsEnum.ABOUT_COMPANY) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISContactIssuerOpenClick,
      location,
    });
  }

  function contactIssuerOpenDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISContactIssuerOpenDone,
    });
  }

  function openPrematureReturnInfo(location: LocationsEnum.MANAGEMENT | LocationsEnum.ISSUE_DETAIL) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenPrematureReturnInfo,
      location,
    });
  }

  function openInvestmentTransferInfo(location: LocationsEnum.MANAGEMENT | LocationsEnum.ISSUE_DETAIL) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISOpenInvestmentTransferInfo,
      location,
    });
  }

  function forgotPasswordClick(location: LocationsEnum.LOGIN | LocationsEnum.FORGOTTEN_PASSWORD_SUCCESS) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISForgotPasswordClick,
      location,
    });
  }

  function createAccountClick(location: LocationsEnum.LOGIN | LocationsEnum.FORGOTTEN_PASSWORD) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISCreateAccountClick,
      location,
    });
  }

  function loginClick() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISLoginClick,
      location,
    });
  }

  function loginDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISLoginDone,
    });
  }

  function callClick(location: LocationsEnum.LOGIN_HEADER
    | LocationsEnum.LOGIN_FOOTER
    | LocationsEnum.FORGOTTEN_PASSWORD_HEADER
    | LocationsEnum.FORGOTTEN_PASSWORD_FOOTER
    | LocationsEnum.FORGOTTEN_PASSWORD_SUCCESS_HEADER
    | LocationsEnum.FORGOTTEN_PASSWORD_SUCCESS_FOOTER
    | LocationsEnum.REGISTER_HEADER
    | LocationsEnum.REGISTER_FOOTER
    | LocationsEnum.REGISTER_PASSWORD_HEADER
    | LocationsEnum.REGISTER_PASSWORD_FOOTER
    | LocationsEnum.REGISTER_CONFIRM_HEADER
    | LocationsEnum.REGISTER_CONFIRM_FOOTER
    | LocationsEnum.REGISTER_SUCCESS_HEADER
    | LocationsEnum.REGISTER_SUCCESS_FOOTER
    | LocationsEnum.RESET_PASSWORD_HEADER
    | LocationsEnum.RESET_PASSWORD_FOOTER
    | LocationsEnum.FOOTER
    | LocationsEnum.MANAGEMENT
    | LocationsEnum.PREMATURE_INFO
    | LocationsEnum.INVESTMENT_TRANSFER_INFO
  ) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISCallClick,
      location
    });
  }

  function emailClick(location: LocationsEnum.LOGIN_FOOTER
    | LocationsEnum.FORGOTTEN_PASSWORD_FOOTER
    | LocationsEnum.FORGOTTEN_PASSWORD_SUCCESS_FOOTER
    | LocationsEnum.REGISTER_FOOTER
    | LocationsEnum.REGISTER_PASSWORD_FOOTER
    | LocationsEnum.REGISTER_CONFIRM_FOOTER
    | LocationsEnum.REGISTER_SUCCESS_FOOTER
    | LocationsEnum.RESET_PASSWORD_FOOTER
    | LocationsEnum.FOOTER
    | LocationsEnum.MANAGEMENT
    | LocationsEnum.PREMATURE_INFO
    | LocationsEnum.INVESTMENT_TRANSFER_INFO
  ) {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISEmailClick,
      location
    });
  }

  function forgotPasswordDone() {
    gtm?.trackEvent({
      event: DataLayerEventsEnum.ISForgotPasswordDone,
    });
  }

  return {
    addBondClick,
    searchMyBondsJames,
    bondsSyncOnDone,
    searchIssueManually,
    chooseIssueManually,
    backManualAdd,
    addIssueManuallyDone,
    turnBondsSyncOff,
    turnMonthlyMailing,
    turnOffersMailing,
    turnInstantNotifications,
    changePasswordClick,
    changePasswordDone,
    logout,
    openIssueDetail,
    openReturnDetail,
    openPortfolio,
    openReturns,
    openUpdates,
    openSettings,
    reportMissingIssueClick,
    reportMissingIssueDone,
    closeConfirmationScreen,
    openIssueManagement,
    contactIssuerOpenClick,
    contactIssuerOpenDone,
    openPrematureReturnInfo,
    openInvestmentTransferInfo,
    forgotPasswordClick,
    createAccountClick,
    loginClick,
    loginDone,
    callClick,
    emailClick,
    forgotPasswordDone,
  };
}
